import { useLanguageContext } from "../../../context/LanguageContext";
import { StyledInput } from "../../../styles/InputStyles";
import InputWrapper from "../../../components/input/wrapper/InputWrapper";
import {
  StyledFlexColumn,
  StyledFlexItem,
  StyledFlexRow,
} from "../../../styles/LayoutStyles";
import OverlayLoader from "../../../components/loader/OverlayLoader/OverlayLoader";
import FormContainer from "../../../components/formContainer/FormContainer";
import EVAApiHelper from "../../../helpers/EVAApiHelper";
import MultiSelect from "../../../components/select/main/multi/MultiSelect";
import Select from "../../../components/select/main/Select";
import LocationFormSelect from "../../../components/select/custom/location/form/LocationFormSelect";
import LanguageFormSelect from "../../../components/select/custom/language/form/LanguageFormSelect";

function EmployeePageView({
  employee,
  employeeData,
  parkingLocations,
  allergens,
  departments,
  onLocationIdChange,
  onRegistrationCodeChange,
  onFirstNameChange,
  onLastNameChange,
  onTitleChange,
  onEmailChange,
  onAllergensChange,
  onDepartmentChange,
  onLanguageChange,
  onEmergencyResponseOfficerChange,
  onPhoneNumberChange,
  onCommunicationChange,
  onLicensePlateChange,
  onParkingLocationsChange,
  onCancel,
  onSubmit,
  submitError,
}) {
  const { translate } = useLanguageContext();

  return (
    <OverlayLoader groups="employee_page_fetch">
      <FormContainer
        onCancel={onCancel}
        onSubmit={onSubmit}
        submitError={submitError}
        submitLoadingGroup={"employee_page_save"}
      >
        <StyledFlexColumn>
          <InputWrapper
            label={translate("eva.main.entities.location")}
            error={EVAApiHelper.getResponseError("location_id", submitError)}
          >
            <LocationFormSelect
              value={employeeData.location_id}
              onChange={onLocationIdChange}
              disabled={employee !== undefined}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.validation.attributes.language_id")}
            error={EVAApiHelper.getResponseError("language_id", submitError)}
          >
            <LanguageFormSelect
              value={employeeData.language_id}
              onChange={onLanguageChange}
              nullable
            />
          </InputWrapper>

          <InputWrapper
            label={translate(
              "eva.main.validation.attributes.registration_code",
            )}
            error={EVAApiHelper.getResponseError(
              "registration_code",
              submitError,
            )}
          >
            <StyledInput
              type="text"
              placeholder={translate(
                "eva.main.validation.attributes.registration_code",
              )}
              value={employeeData.registration_code}
              onChange={onRegistrationCodeChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.validation.attributes.title")}
            required
            error={EVAApiHelper.getResponseError("title", submitError)}
          >
            <StyledInput
              type="text"
              placeholder={translate("eva.main.validation.attributes.title")}
              value={employeeData.title}
              onChange={onTitleChange}
            />
          </InputWrapper>

          <StyledFlexRow>
            <StyledFlexItem>
              <InputWrapper
                label={translate("eva.main.validation.attributes.first_name")}
                required
                error={EVAApiHelper.getResponseError("first_name", submitError)}
              >
                <StyledInput
                  type="text"
                  placeholder={translate(
                    "eva.main.validation.attributes.first_name",
                  )}
                  value={employeeData.first_name}
                  onChange={onFirstNameChange}
                />
              </InputWrapper>
            </StyledFlexItem>

            <StyledFlexItem>
              <InputWrapper
                label={translate("eva.main.validation.attributes.last_name")}
                required
                error={EVAApiHelper.getResponseError("last_name", submitError)}
              >
                <StyledInput
                  type="text"
                  placeholder={translate(
                    "eva.main.validation.attributes.last_name",
                  )}
                  value={employeeData.last_name}
                  onChange={onLastNameChange}
                />
              </InputWrapper>
            </StyledFlexItem>
          </StyledFlexRow>

          <InputWrapper
            label={translate("eva.main.validation.attributes.email")}
            required
            error={EVAApiHelper.getResponseError("email", submitError)}
          >
            <StyledInput
              type="email"
              placeholder={translate("eva.main.validation.placeholders.email")}
              value={employeeData.email}
              onChange={onEmailChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.validation.attributes.phone_number")}
            error={EVAApiHelper.getResponseError("phone_number", submitError)}
          >
            <StyledInput
              type="text"
              placeholder={translate(
                "eva.main.validation.placeholders.phone_number",
              )}
              value={employeeData.phone_number}
              onChange={onPhoneNumberChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.validation.attributes.communication")}
            error={EVAApiHelper.getResponseError("communication", submitError)}
          >
            <MultiSelect
              options={[
                {
                  value: "sms",
                  label: translate("eva.main.communication_types.sms"),
                },
                {
                  value: "call",
                  label: translate("eva.main.communication_types.call"),
                },
              ]}
              value={employeeData.communication}
              onChange={onCommunicationChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.validation.attributes.department_id")}
            error={EVAApiHelper.getResponseError("department_id", submitError)}
          >
            <Select
              options={departments.map((department) => {
                return { value: department.id, label: department.name };
              })}
              nullable={true}
              value={employeeData.department_id}
              onChange={onDepartmentChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate(
              "eva.main.validation.attributes.emergency_response_officer",
            )}
            error={EVAApiHelper.getResponseError(
              "emergency_response_officer",
              submitError,
            )}
          >
            <Select
              options={[
                { value: true, label: translate("eva.main.general.yes") },
                { value: false, label: translate("eva.main.general.no") },
              ]}
              value={employeeData.emergency_response_officer}
              onChange={onEmergencyResponseOfficerChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.entities.allergens")}
            error={EVAApiHelper.getResponseErrorArray(
              "allergens",
              submitError,
              (index) =>
                translate(`eva.main.allergens.${allergens[index].name}`),
            )}
          >
            <MultiSelect
              options={allergens.map((allergen) => {
                return {
                  value: allergen.id,
                  label: translate(`eva.main.allergens.${allergen.name}`),
                };
              })}
              value={employeeData.allergens}
              onChange={onAllergensChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.validation.attributes.license_plate")}
            error={EVAApiHelper.getResponseError("license_plate", submitError)}
          >
            <StyledInput
              type="text"
              placeholder={translate(
                "eva.main.validation.attributes.license_plate",
              )}
              value={employeeData.license_plate}
              onChange={onLicensePlateChange}
            />
          </InputWrapper>

          <InputWrapper
            label={translate(
              "eva.main.validation.attributes.parking_locations",
            )}
          >
            <MultiSelect
              value={employeeData.parking_locations}
              options={parkingLocations.map((location) => ({
                value: location.id,
                label: location.name,
              }))}
              onChange={onParkingLocationsChange}
            />
          </InputWrapper>
        </StyledFlexColumn>
      </FormContainer>
    </OverlayLoader>
  );
}

export default EmployeePageView;
